import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Save,
  Loader2,
  FileText,
  Eye,
  EyeOff,
  Trash2,
  GripVertical,
  Pencil,
  Image,
  Type,
  List,
  Phone,
  MessageSquare,
  ArrowUp,
  ArrowDown,
  ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_TYPES = [
  { id: 'hero', label: 'Hero / Bannière', icon: Image },
  { id: 'text', label: 'Texte', icon: Type },
  { id: 'features', label: 'Caractéristiques', icon: List },
  { id: 'cta', label: 'Appel à l\'action', icon: MessageSquare },
  { id: 'contact', label: 'Formulaire contact', icon: Phone },
  { id: 'custom', label: 'HTML personnalisé', icon: FileText },
];

export default function CMSPagesManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pageForm, setPageForm] = useState({
    title: '',
    slug: '',
    meta_description: '',
    sections: [],
    is_published: true,
    show_in_menu: true,
    menu_order: 0,
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cms/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Erreur lors du chargement des pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pageForm.title || !pageForm.slug) {
      toast.error('Titre et slug sont obligatoires');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingPage 
        ? `${API_URL}/api/cms/pages/${editingPage.id}`
        : `${API_URL}/api/cms/pages`;
      
      const response = await fetch(url, {
        method: editingPage ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(pageForm)
      });

      if (response.ok) {
        toast.success(editingPage ? 'Page mise à jour' : 'Page créée');
        setDialogOpen(false);
        resetForm();
        fetchPages();
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pageId) => {
    if (!confirm('Supprimer cette page ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cms/pages/${pageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Page supprimée');
        fetchPages();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingPage(null);
    setPageForm({
      title: '',
      slug: '',
      meta_description: '',
      sections: [],
      is_published: true,
      show_in_menu: true,
      menu_order: 0,
    });
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setPageForm({
      title: page.title,
      slug: page.slug,
      meta_description: page.meta_description || '',
      sections: page.sections || [],
      is_published: page.is_published,
      show_in_menu: page.show_in_menu,
      menu_order: page.menu_order || 0,
    });
    setDialogOpen(true);
  };

  const addSection = (type) => {
    const newSection = {
      id: `section-${Date.now()}`,
      type,
      title: '',
      subtitle: '',
      content: '',
      image_url: '',
      items: [],
      settings: {},
      visible: true,
      order: pageForm.sections.length,
    };
    setPageForm({ ...pageForm, sections: [...pageForm.sections, newSection] });
  };

  const updateSection = (index, updates) => {
    const newSections = [...pageForm.sections];
    newSections[index] = { ...newSections[index], ...updates };
    setPageForm({ ...pageForm, sections: newSections });
  };

  const removeSection = (index) => {
    const newSections = pageForm.sections.filter((_, i) => i !== index);
    setPageForm({ ...pageForm, sections: newSections });
  };

  const moveSection = (index, direction) => {
    const newSections = [...pageForm.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setPageForm({ ...pageForm, sections: newSections });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Pages du site</h2>
          <p className="text-sm text-slate-500">Créez et gérez les pages de votre site</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-yellow-500 hover:bg-yellow-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle page
        </Button>
      </div>

      {/* Pages List */}
      <div className="grid gap-4">
        {pages.length === 0 ? (
          <Card className="p-8 text-center text-slate-500">
            Aucune page créée. Cliquez sur "Nouvelle page" pour commencer.
          </Card>
        ) : (
          pages.map((page) => (
            <Card key={page.id} className="hover:shadow-md transition">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <FileText className="w-8 h-8 text-slate-400" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-800">{page.title}</h3>
                        {page.is_published ? (
                          <Badge className="bg-green-100 text-green-700">Publié</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">Brouillon</Badge>
                        )}
                        {page.show_in_menu && (
                          <Badge variant="outline">Menu</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">/portail/{page.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/portail/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-slate-100 rounded"
                    >
                      <ExternalLink className="w-4 h-4 text-slate-500" />
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(page)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(page.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPage ? 'Modifier la page' : 'Nouvelle page'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titre de la page *</Label>
                <Input
                  value={pageForm.title}
                  onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                  placeholder="À propos de nous"
                />
              </div>
              <div className="space-y-2">
                <Label>Slug (URL) *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">/portail/</span>
                  <Input
                    value={pageForm.slug}
                    onChange={(e) => setPageForm({ 
                      ...pageForm, 
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                    })}
                    placeholder="a-propos"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description SEO</Label>
              <Textarea
                value={pageForm.meta_description}
                onChange={(e) => setPageForm({ ...pageForm, meta_description: e.target.value })}
                placeholder="Description pour les moteurs de recherche..."
                rows={2}
              />
            </div>

            {/* Options */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={pageForm.is_published}
                  onCheckedChange={(checked) => setPageForm({ ...pageForm, is_published: checked })}
                />
                <span>Publier la page</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={pageForm.show_in_menu}
                  onCheckedChange={(checked) => setPageForm({ ...pageForm, show_in_menu: checked })}
                />
                <span>Afficher dans le menu</span>
              </label>
            </div>

            {/* Sections */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base">Sections de contenu</Label>
                <Select onValueChange={addSection}>
                  <SelectTrigger className="w-[200px]">
                    <Plus className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Ajouter une section" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTION_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                {pageForm.sections.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                    Aucune section. Utilisez le bouton ci-dessus pour ajouter du contenu.
                  </p>
                ) : (
                  pageForm.sections.map((section, index) => (
                    <Card key={section.id} className="border-slate-200">
                      <CardHeader className="py-3 px-4 bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-slate-400" />
                            <Badge variant="outline">
                              {SECTION_TYPES.find(t => t.id === section.type)?.label || section.type}
                            </Badge>
                            {section.title && (
                              <span className="text-sm text-slate-600">{section.title}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => moveSection(index, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => moveSection(index, 'down')}
                              disabled={index === pageForm.sections.length - 1}
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => updateSection(index, { visible: !section.visible })}
                            >
                              {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-500"
                              onClick={() => removeSection(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Titre</Label>
                            <Input
                              value={section.title || ''}
                              onChange={(e) => updateSection(index, { title: e.target.value })}
                              placeholder="Titre de la section"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Sous-titre</Label>
                            <Input
                              value={section.subtitle || ''}
                              onChange={(e) => updateSection(index, { subtitle: e.target.value })}
                              placeholder="Sous-titre (optionnel)"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Contenu</Label>
                          <Textarea
                            value={section.content || ''}
                            onChange={(e) => updateSection(index, { content: e.target.value })}
                            placeholder="Contenu de la section..."
                            rows={4}
                          />
                        </div>
                        {(section.type === 'hero' || section.type === 'cta') && (
                          <div className="space-y-2">
                            <Label>URL de l'image</Label>
                            <Input
                              value={section.image_url || ''}
                              onChange={(e) => updateSection(index, { image_url: e.target.value })}
                              placeholder="https://..."
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editingPage ? 'Mettre à jour' : 'Créer la page'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
